import React from 'react';
import { CloseOutline } from 'antd-mobile-icons';
import { t } from '../../../i18n';
import { PopupType, ScanPromptInfo } from './types';

/**
 * `ScanResultPrompt` - 受控的 PDA 扫描结果展示组件。
 *
 * 纯视觉，无副作用；状态由 `useScanResultPrompt` 管理。
 *
 * 4 种 popupType 对应不同视觉（参考 PRD 设计稿）：
 *   - INTERCEPT  整屏红色边框 + 红底中部弹窗（带 × 按钮），弹窗体含「尾号 + barCode 末6位 / title 大字 / content 小字」
 *   - NEED_ACTION 整屏橙色边框 + 橙底中部弹窗，同上结构
 *   - SUCCESS    整屏绿色边框，无弹窗内容（由 hook 1.5s 自动清掉）
 *   - NOTICE     黑底中部弹窗（带 × 按钮 + 尾号 + barCode 末6位 + content），无色框，由 hook 1.5s 自动清掉
 *
 * 边框层使用 `pointer-events-none`，不阻塞 PDA 硬件扫描及非弹窗区域的点击；
 * 弹窗本体内层使用 `pointer-events-auto`，确保 × 按钮可点。
 */

interface ScanResultPromptProps {
    prompt: ScanPromptInfo | null;
    /** 引发本次弹窗的 barCode（运单号 / 包牌号），弹窗体显示末 6 位；null 表示不显示 */
    barCode?: string | null;
    /** 用户点击 × 或外部触发时调用 */
    onClose: () => void;
}

/** 取 barCode 末 6 位（不足 6 位则取全部）；null / 空串返回空串 */
const lastSix = (s?: string | null): string => {
    if (!s) return '';
    return s.length > 6 ? s.slice(-6) : s;
};

interface ColoredPromptBodyProps {
    title?: string;
    content: string;
    barCode?: string | null;
}

/** 红 / 橙弹窗共用的中部内容（尾号 + barCode 末6位 + title + content） */
const ColoredPromptBody: React.FC<ColoredPromptBodyProps> = ({ title, content, barCode }) => {
    const tail = lastSix(barCode);
    return (
        <div className="px-4 py-4 text-center whitespace-pre-line break-words text-white">
            {tail && (
                <>
                    <div className="text-sm opacity-80 mb-1">{t('pda.scan.barCodeTailLabel')}</div>
                    <div className="text-3xl font-bold mb-2 tracking-wider">{tail}</div>
                </>
            )}
            {title && <div className="text-3xl font-bold mb-2">{title}</div>}
            {content && <div className="text-sm opacity-90 whitespace-pre-line text-center">{content.replace(/\\n/g, '\n')}</div>}
        </div>
    );
};

export const ScanResultPrompt: React.FC<ScanResultPromptProps> = ({ prompt, barCode, onClose }) => {
    if (!prompt) return null;

    switch (prompt.popupType) {
        case PopupType.SUCCESS:
            return (
                <div
                    className="fixed inset-0 border-8 border-green-500 pointer-events-none z-[60] bg-green-50 bg-opacity-20"
                    aria-label="scan-success-frame"
                />
            );

        case PopupType.INTERCEPT:
            return (
                <div className="fixed inset-0 z-[60] pointer-events-none" aria-label="scan-intercept">
                    <div className="absolute inset-0 border-8 border-red-500" />
                    <div className="absolute inset-0 flex items-center justify-center px-6">
                        <div className="pointer-events-auto w-full max-w-xs bg-red-500 text-white rounded-lg shadow-xl">
                            <div className="relative flex items-center justify-center px-4 py-2">
                                <div className="text-sm font-medium opacity-90">⊗ {t('pda.scan.popupType.intercept')}</div>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    aria-label="close-intercept"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 opacity-90"
                                >
                                    <CloseOutline fontSize={18} />
                                </button>
                            </div>
                            <ColoredPromptBody title={prompt.title} content={prompt.content} barCode={barCode} />
                        </div>
                    </div>
                </div>
            );

        case PopupType.NEED_ACTION:
            return (
                <div className="fixed inset-0 z-[60] pointer-events-none" aria-label="scan-need-action">
                    <div className="absolute inset-0 border-8 border-orange-500" />
                    <div className="absolute inset-0 flex items-center justify-center px-6">
                        <div className="pointer-events-auto w-full max-w-xs bg-orange-500 text-white rounded-lg shadow-xl">
                            <div className="relative flex items-center justify-center px-4 py-2">
                                <div className="text-sm font-medium opacity-90">⚠ {t('pda.scan.popupType.needAction')}</div>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    aria-label="close-need-action"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 opacity-90"
                                >
                                    <CloseOutline fontSize={18} />
                                </button>
                            </div>
                            <ColoredPromptBody title={prompt.title} content={prompt.content} barCode={barCode} />
                        </div>
                    </div>
                </div>
            );

        case PopupType.SUCCESS_WITH_NOTICE:
            return (
                <div className="fixed inset-0 z-[60] pointer-events-none" aria-label="scan-success-with-notice">
                    <div className="absolute inset-0 border-8 border-orange-500" />
                    <div className="absolute inset-0 flex items-center justify-center px-6">
                        <div className="pointer-events-auto w-full max-w-xs bg-orange-500 text-white rounded-lg shadow-xl">
                            <div className="relative flex items-center justify-center px-4 py-2">
                                <div className="text-sm font-medium opacity-90">✓ {t('pda.scan.popupType.successWithNotice')}</div>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    aria-label="close-success-with-notice"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 opacity-90"
                                >
                                    <CloseOutline fontSize={18} />
                                </button>
                            </div>
                            <ColoredPromptBody title={prompt.title} content={prompt.content} barCode={barCode} />
                        </div>
                    </div>
                </div>
            );

        case PopupType.NOTICE: {
            const tail = lastSix(barCode);
            return (
                <div className="fixed inset-0 z-[60] pointer-events-none flex items-center justify-center px-6" aria-label="scan-notice">
                    <div className="pointer-events-auto relative w-full max-w-xs bg-black bg-opacity-85 text-white rounded-lg shadow-xl px-4 py-5">
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="close-notice"
                            className="absolute right-2 top-2 flex items-center justify-center w-6 h-6 opacity-90"
                        >
                            <CloseOutline fontSize={18} />
                        </button>
                        <div className="text-center whitespace-pre-line break-words">
                            {tail && (
                                <>
                                    <div className="text-sm opacity-80 mb-1">{t('pda.scan.barCodeTailLabel')}</div>
                                    <div className="text-3xl font-bold mb-2 tracking-wider">{tail}</div>
                                </>
                            )}
                            {prompt.title && <div className="text-xl font-bold whitespace-pre-line text-center">{prompt.title}</div>}
                            {!prompt.title && prompt.content && <div className="text-xl font-bold whitespace-pre-line text-center">{prompt.content.replace(/\\n/g, '\n')}</div>}
                        </div>
                    </div>
                </div>
            );
        }

        default: {
            // 未知 popupType 兜底：黑底 NOTICE 样式，避免操作员扫描后整屏无反馈
            const tail = lastSix(barCode);
            return (
                <div className="fixed inset-0 z-[60] pointer-events-none flex items-center justify-center px-6" aria-label="scan-unknown">
                    <div className="pointer-events-auto relative w-full max-w-xs bg-black bg-opacity-85 text-white rounded-lg shadow-xl px-4 py-5">
                        <button type="button" onClick={onClose} aria-label="close-unknown"
                            className="absolute right-2 top-2 flex items-center justify-center w-6 h-6 opacity-90">
                            <CloseOutline fontSize={18} />
                        </button>
                        <div className="text-center whitespace-pre-line break-words">
                            {tail && (
                                <>
                                    <div className="text-sm opacity-80 mb-1">{t('pda.scan.barCodeTailLabel')}</div>
                                    <div className="text-3xl font-bold mb-2 tracking-wider">{tail}</div>
                                </>
                            )}
                            {prompt.title && <div className="text-xl font-bold whitespace-pre-line text-center">{prompt.title}</div>}
                            {!prompt.title && prompt.content && <div className="text-xl font-bold whitespace-pre-line text-center">{prompt.content.replace(/\\n/g, '\n')}</div>}
                        </div>
                    </div>
                </div>
            );
        }
    }
};
